import * as Sentry from '@sentry/react';
import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { OutboundTaskTypeKey, OutboundTaskType, TaskComment, TaskEvent, client } from '../../../../api';
import type { NewCommentFile } from '../../../../api';
import { MergedEligibilityBanner } from '../../actions/MergedEligibilityBanner';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { ActionCard } from '../../../ActionCard';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { tasksQueryKey } from '../../../../queries';
import {
  InsuranceInput,
  isInsuranceInputValid,
  getManualEntryErrors,
  INITIAL_INSURANCE_FORM_DATA,
  type EntryMethod,
  type InsuranceFormData,
} from '../../shared/InsuranceInput';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { StructuredTaskViewWrapper } from '..';

/**
 * Component for rendering the Unable to Verify Insurance structured task.
 */
const UnableToVerifyInsuranceTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);

  // Insurance form state
  const [entryMethod, setEntryMethod] = useState<EntryMethod>('upload');
  const [uploadedFiles, setUploadedFiles] = useState<NewCommentFile[]>([]);
  const [formData, setFormData] = useState<InsuranceFormData>(INITIAL_INSURANCE_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof InsuranceFormData, boolean>>>({});
  const [showUploadError, setShowUploadError] = useState(false);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';

  const canSubmit = isInsuranceInputValid(entryMethod, uploadedFiles, formData);

  const handleSubmit = async () => {
    if (!canSubmit) {
      if (entryMethod === 'manual') {
        setFieldErrors(getManualEntryErrors(formData));
      } else {
        setShowUploadError(true);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const formDataToSubmit = new FormData();

      if (entryMethod === 'upload') {
        formDataToSubmit.append('task_response', 'upload_insurance_card');

        const formFilesDocumentTypes: Record<string, string> = {};
        uploadedFiles.forEach((fileInfo) => {
          formDataToSubmit.append('files[]', fileInfo.file);
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });
        formDataToSubmit.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      } else {
        formDataToSubmit.append('task_response', 'manual_insurance_entry');
        formDataToSubmit.append(
          'additional_input',
          JSON.stringify({
            insurance_data: {
              insurance_company_name: formData.insuranceCompanyName,
              insurance_type: formData.insuranceType,
              cardholder_id: formData.cardholderId,
              rx_bin: formData.rxBin || null,
              pcn: formData.pcn || null,
              group_id: formData.groupId || null,
            },
          }),
        );
      }

      await client.post(`tasks/${taskId}/respond`, formDataToSubmit);
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    } catch (error) {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!task.timeline || !task.timeline.length) {
    Sentry.captureMessage('No initial comment found for Unable to Verify Insurance task', {
      level: 'error',
      extra: {
        taskId,
      },
    });
    return <PageLoadError />;
  }

  const initialComment = task.timeline[0] as TaskComment | undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline.length === 1 || !initialComment) {
      Sentry.captureMessage('No completion event found for Unable to Verify Insurance task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    } else {
      const timelineItem = task.timeline[1];
      completionEvent = timelineItem.item_type === 'event' ? (timelineItem as TaskEvent) : undefined;
    }
  }

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={task.type as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: { data: drug },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion message for completed tasks */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions */}
        <div className="mb-6 flex flex-col gap-4">
          <TaskInstructionSummary>Provide patient's insurance information:</TaskInstructionSummary>
          {initialComment && initialComment.item_type === 'comment' && (
            <Typography styledAs="bodySmallDMSans">{initialComment.message}</Typography>
          )}
        </div>

        {/* Insurance form - only show when task is not completed */}
        {!isCompleted && (
          <>
            <MergedEligibilityBanner mergedEligibilities={task.merged_eligibilities} />
            <ActionCard>
              <div className="flex w-full flex-col gap-4">
                <div className="mb-4">
                  <InsuranceInput
                    entryMethod={entryMethod}
                    onEntryMethodChange={(method) => {
                      setEntryMethod(method);
                      setShowUploadError(false);
                    }}
                    uploadedFiles={uploadedFiles}
                    onFilesChange={(files) => {
                      setUploadedFiles(files);
                      if (files.length > 0) setShowUploadError(false);
                    }}
                    formData={formData}
                    onFormDataChange={setFormData}
                    fieldErrors={fieldErrors}
                    onFieldErrorsChange={setFieldErrors}
                    showUploadError={showUploadError}
                  />
                </div>

                <ButtonRow
                  align={Alignment.SpaceBetween}
                  fullWidth
                  className="border-t-grayscale-100 pt-4 border-t-solid"
                >
                  <ButtonRow className="!gap-4">
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => setShowDeclineModal(true)}
                      disabled={isSubmitting}
                    >
                      Cancel prescription
                    </Button>
                    <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
                  </ButtonRow>
                  <Button variant="primary" size="sm" onClick={handleSubmit} isLoading={isSubmitting}>
                    Submit
                  </Button>
                </ButtonRow>
              </div>
            </ActionCard>
          </>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={OutboundTaskTypeKey.UNABLE_TO_VERIFY_INSURANCE}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={OutboundTaskTypeKey.UNABLE_TO_VERIFY_INSURANCE}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default UnableToVerifyInsuranceTaskView;
